<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Transferts de stock ({{ totalRows }})</h1>
          <small class="text-muted">Expédiez des produits vers un autre site, puis réceptionnez-les côté destination.</small>
        </div>
        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['stock_transfers', 'StockTransfer', 'StockTransferItem']" label="Transferts de stock" />

          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openCreatePage">
            <i class="fa fa-right-left"></i>
            <span class="btn-label ms-1">Nouveau transfert</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2">{{ successMessage }}</div>

      <BootstrapDataTable
        :response="transfersResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeleteStockTransfers"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectStockTransferForBulkDelete"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        state-key="stock-transfers.list"
        empty-text="Aucun transfert trouvé."
        @row-click="openTransferDetailPage"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchTransfers"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeleteStockTransfers"
          >
            <i class="fa fa-trash me-1" aria-hidden="true"></i>Supprimer {{ selectedCount }}
          </button>
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
        </template>
        <template #[`cell-source_shop.name`]="{ row }">
          {{ row.source_shop?.name || '-' }}
        </template>
        <template #[`cell-destination_shop.name`]="{ row }">
          {{ row.destination_shop?.name || '-' }}
        </template>
        <template #cell-created_at="{ row }">
          {{ $format.datetime(row.created_at) }}
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { extractApiTotal, extractApiList } from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

export default {
  name: 'StockTransfersView',
  mixins: [bulkActionViewMixin],
  data() {
    return {
      transfersResponse: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'created_at',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'source_shop.name', label: 'Source' },
        { key: 'destination_shop.name', label: 'Destination' },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'items_count', label: 'Lignes', class: 'text-end', headerClass: 'text-end' },
        { key: 'created_at', label: 'Créé le', sortable: true },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    canBulkDeleteStockTransfers() {
      return hasPermission(this.currentUser, 'stock_transfers.delete');
    },
    currentShopId() {
      return this.$store?.getters?.['shop/currentShopId']
        || this.$store?.state?.shop?.currentShopId
        || null;
    },
    totalRows() {
      return extractApiTotal(this.transfersResponse, this.transferRows.length);
    },
    transferRows() {
      return extractApiList(this.transfersResponse);
    },
  },
  mounted() {
    this.fetchTransfers();
  },
  methods: {
    canSelectStockTransferForBulkDelete(transfer) {
      const sourceShopId = transfer?.source_shop_id || transfer?.source_shop?.id;

      return transfer?.status === 'draft'
        && Number(sourceShopId || 0) === Number(this.currentShopId || 0);
    },

    async bulkDeleteStockTransfers() {
      await this.performBulkAction({
        url: '/stock-transfers/bulk',
        method: 'delete',
        itemLabel: 'transfert',
        confirmation: (count) => ({
          header: 'Supprimer les transferts',
          message: `Supprimer les ${count} transferts brouillon sélectionnés ?`,
          acceptLabel: `Supprimer ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchTransfers(),
      });
    },

    async fetchTransfers() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const res = await this.$api.get('/stock-transfers', {
          params: {
            paginate: 1,
            page: this.table.page,
            per_page: this.table.perPage,
            sort_by: this.table.sortBy,
            sort_dir: this.table.sortDir,
          },
        });
        this.transfersResponse = res.data;
      } catch (error) {
        this.errorMessage = 'Impossible de charger les transferts.';
      } finally {
        this.isLoading = false;
      }
    },
    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchTransfers();
    },
    handlePageChange(page) {
      this.table.page = page;
      this.fetchTransfers();
    },
    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchTransfers();
    },
    openCreatePage() {
      this.$router.push({ name: 'StockTransferCreate' });
    },
    openTransferDetailPage(row) {
      this.$router.push({ name: 'StockTransferShow', params: { id: row.id } });
    },
    statusLabel(status) {
      return {
        draft: 'Brouillon',
        sent: 'Expédié',
        received: 'Réceptionné',
        cancelled: 'Annulé',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-secondary',
        sent: 'bg-warning text-dark',
        received: 'bg-success',
        cancelled: 'bg-danger',
      }[status] || 'bg-secondary';
    },
  },
};
</script>
